import React, { useState, useEffect, useCallback, useRef } from 'react';
import { attachmentsApi } from '../services/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Paperclip, Upload, Trash2, FileText, Image, File, Download } from 'lucide-react';
import { toast } from 'sonner';

const fileIcon = (ext) => {
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return <Image className="w-4 h-4 text-green-600" />;
  if (['pdf', 'docx', 'doc', 'txt'].includes(ext)) return <FileText className="w-4 h-4 text-blue-600" />;
  return <File className="w-4 h-4 text-gray-500" />;
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const Attachments = ({ entityType, entityId }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchAttachments = useCallback(async () => {
    if (!entityId) return;
    try {
      const res = await attachmentsApi.getAll(entityType, entityId);
      setAttachments(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [entityType, entityId]);

  useEffect(() => { fetchAttachments(); }, [fetchAttachments]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    setUploading(true);
    try {
      await attachmentsApi.upload(file, entityType, entityId);
      toast.success('File attached');
      fetchAttachments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (attachmentId) => {
    try {
      await attachmentsApi.delete(attachmentId);
      setAttachments(attachments.filter(a => a.id !== attachmentId));
      toast.success('Attachment removed');
    } catch { toast.error('Failed to remove attachment'); }
  };

  return (
    <div data-testid="attachments-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-navy/50" />
          <span className="text-sm font-semibold text-[#1A2B5E]">Attachments</span>
          <Badge variant="outline" className="text-xs">{attachments.length}</Badge>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleUpload}
            className="hidden"
            data-testid="attachment-file-input"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-attachment-btn"
          >
            <Upload className="w-3.5 h-3.5 mr-1" />
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {loading ? (
          <p className="text-sm text-navy/40">Loading attachments...</p>
        ) : attachments.length === 0 ? (
          <p className="text-sm text-navy/40">No attachments yet</p>
        ) : attachments.map(att => (
          <div
            key={att.id}
            className="flex items-center gap-3 p-2.5 rounded-lg border border-navy/10 bg-gray-50/50 group hover:border-navy/20 transition-colors"
            data-testid={`attachment-${att.id}`}
          >
            {fileIcon(att.file_type)}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1A2B5E] truncate">{att.filename}</p>
              <p className="text-[10px] text-navy/40">
                {formatSize(att.file_size)} &middot; {att.uploaded_by_name} &middot; {new Date(att.created_at).toLocaleDateString()}
              </p>
            </div>
            <a
              href={attachmentsApi.download(att.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded hover:bg-navy/5 text-navy/40 hover:text-[#106EFD] transition-colors"
              data-testid={`download-${att.id}`}
            >
              <Download className="w-4 h-4" />
            </a>
            <button
              onClick={() => handleDelete(att.id)}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded hover:bg-red-50 text-navy/30 hover:text-red-500"
              data-testid={`delete-attachment-${att.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Attachments;
